import { Grid } from "@material-ui/core";
import moment from "moment-timezone";
import { useEffect } from "react";
import { ArrayInput, AutocompleteArrayInput, BooleanField, BooleanInput, Create, CreateButton, Datagrid, DeleteWithConfirmButton, Edit, EditButton, Filter, FormDataConsumer, FunctionField, List, NumberInput, ReferenceArrayInput, SelectInput, Show, SimpleForm, SimpleFormIterator, SimpleShowLayout, TextField, TextInput, TopToolbar, required, usePermissions, useRecordContext } from "react-admin";
import { useForm } from "react-final-form";
import { MyDateField } from "../MyDateField";
import { MyDateTimeInput } from "../MyDateTimeInput";
import { PlatformCheckboxGroupInput, PlatformChipField } from "./component/platform-components";
import { RegionCheckboxGroupInput, RegionTextField } from "./component/region-components";

const canEditAdsVideo = (permissions) => permissions && permissions.includes("edit_ads_video");
const filterToName = (searchText) => (searchText ? { name_tc: searchText } : {});
const idList = (values) => (Array.isArray(values) ? values.join(", ") : "");
const defaultCategoryProfileClass = "general";
const isStartupAd = (type) => type === "startup-ad";
const isRelationLocked = (formData) => isStartupAd(formData?.type) || formData?.is_homepage === true;

const AdsVideoFilter = (props) => (
  <Filter {...props}>
    <SelectInput label="Type" source="type" alwaysOn choices={[{ id: "transparent-ad", name: "Transparent Ad" }, { id: "startup-ad", name: "Startup Ad" }]} />
    <TextInput label="Media URL" source="media_url" alwaysOn />
  </Filter>
);

const AdsVideoListActions = () => {
  const { permissions } = usePermissions();
  return <TopToolbar>{canEditAdsVideo(permissions) && <CreateButton />}</TopToolbar>;
};

const PermissionEditButton = (props) => {
  const { permissions } = usePermissions();
  const record = useRecordContext();
  if (!record || !record.id) return null;
  return canEditAdsVideo(permissions) ? <EditButton {...props} record={record} /> : null;
};

const PermissionDeleteButton = (props) => {
  const { permissions } = usePermissions();
  const record = useRecordContext();
  if (!record || !record.id) return null;
  return canEditAdsVideo(permissions) ? <DeleteWithConfirmButton {...props} record={record} undoable={false} /> : null;
};

export const AdsVideoList = (props) => (
  <List {...props} sort={{ field: "modified_at", order: "DESC" }} bulkActionButtons={false} filters={<AdsVideoFilter />} actions={<AdsVideoListActions />}>
    <Datagrid>
      <TextField source="id" label="ID" />
      <TextField source="type" />
      <TextField source="media_url" label="Media URL" />
      <FunctionField label="Programme IDs" render={(record) => idList(record.programme_id)} />
      <FunctionField label="Page IDs" render={(record) => idList(record.page_id)} />
      <FunctionField label="Category IDs" render={(record) => idList(record.category_id)} />
      <BooleanField source="is_homepage" label="Is Homepage" />
      <RegionTextField />
      <MyDateField source="start_time" showTime />
      <MyDateField source="end_time" showTime />
      <PermissionEditButton />
      <PermissionDeleteButton />
    </Datagrid>
  </List>
);

const AdsVideoRelations = ({ disabled }) => (
  <>
    <Grid item xs={12} md={4}>
      <ReferenceArrayInput label="Programmes" source="programme_id" reference="programmes-autocomplete" filterToQuery={filterToName} fullWidth>
        <AutocompleteArrayInput optionText="name_tc" optionValue="programme_id" fullWidth disabled={disabled} />
      </ReferenceArrayInput>
    </Grid>
    <Grid item xs={12} md={4}>
      <ReferenceArrayInput label="Pages" source="page_id" reference="mpm-page-autocomplete" filterToQuery={filterToName} fullWidth>
        <AutocompleteArrayInput optionText="name_tc" optionValue="id" fullWidth disabled={disabled} />
      </ReferenceArrayInput>
    </Grid>
    <Grid item xs={12} md={4}>
      <SelectInput source="category_profile_class" label="Category Profile" choices={[{ id: "general", name: "General" }, { id: "kids", name: "Kids" }]} defaultValue={defaultCategoryProfileClass} fullWidth disabled={disabled} />
      <FormDataConsumer>
        {({ formData }) => {
          const profileClass = formData?.category_profile_class || defaultCategoryProfileClass;
          return (
            <ReferenceArrayInput
              label="Categories"
              source="category_id"
              reference="ads-video-categories"
              filter={{ profile_class: profileClass }}
              filterToQuery={(searchText) => ({
                ...(searchText ? { name_tc: searchText } : {}),
                profile_class: profileClass,
              })}
              fullWidth
            >
              <AutocompleteArrayInput optionText="name_tc" optionValue="id" fullWidth disabled={disabled} />
            </ReferenceArrayInput>
          );
        }}
      </FormDataConsumer>
    </Grid>
  </>
);

const AdsVideoTypeGuard = () => {
  const form = useForm();

  useEffect(() => {
    const unsubscribe = form.subscribe(
      ({ values }) => {
        if (isStartupAd(values.type) && values.is_homepage) {
          form.change("is_homepage", false);
        }
      },
      { values: true },
    );

    return unsubscribe;
  }, [form]);

  return null;
};

const validateAdsVideo = (values) => {
  const errors = {};
  if (values.is_homepage && values.type !== "transparent-ad") {
    errors.is_homepage = "Homepage is only allowed for transparent-ad";
  }
  if (values.type === "transparent-ad" && !values.is_homepage && !values.programme_id?.length && !values.page_id?.length && !values.category_id?.length) {
    errors.programme_id = "Choose at least one programme, page, or category for a transparent ad";
  }
  if (values.start_time && values.end_time && moment(values.start_time).valueOf() >= moment(values.end_time).valueOf()) {
    errors.end_time = "End time must be later than start time";
  }
  const periodUnits = values.frequency_cap?.map((frequencyCap) => frequencyCap?.period_unit).filter(Boolean) || [];
  if (new Set(periodUnits).size !== periodUnits.length) {
    errors.frequency_cap = "Each frequency cap must use a different period unit";
  }
  return errors;
};

const toIsoString = (value) => (value ? moment(value).utc().toISOString() : value);
const transformAdsVideo = (values) => {
  const restValues = { ...values };
  delete restValues.region;
  delete restValues.category_profile_class;
  const region = values.region || values.locations?.allowlist?.countries || ["HK", "MO"];
  const relationData = values.is_homepage
    ? {
        programme_id: [],
        page_id: [],
        category_id: [],
      }
    : {
        programme_id: values.programme_id || [],
        page_id: values.page_id || [],
        category_id: values.category_id || [],
      };

  return {
    ...restValues,
    ...relationData,
    frequency_cap: values.frequency_cap?.length ? values.frequency_cap : [{ imp_value: 5, period_value: 1, period_unit: "day" }],
    locations: { allowlist: { countries: region } },
    start_time: toIsoString(values.start_time),
    end_time: toIsoString(values.end_time),
  };
};

const AdsVideoRegionInput = () => {
  const form = useForm();
  const record = useRecordContext();

  useEffect(() => {
    const region = form.getState().values.region;
    if (region && region.length > 0) {
      return;
    }

    const recordRegion = record?.locations?.allowlist?.countries;
    if (recordRegion && recordRegion.length > 0) {
      form.change("region", recordRegion);
    }
  }, [form, record]);

  return <RegionCheckboxGroupInput noDefaultValue={true} />;
};

const AdsVideoForm = () => (
  <Grid container spacing={1}>
    <AdsVideoTypeGuard />
    <Grid item xs={12} md={6}>
      <SelectInput source="type" validate={required()} fullWidth choices={[{ id: "transparent-ad", name: "Transparent Ad" }, { id: "startup-ad", name: "Startup Ad" }]} />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextInput label="Media URL" source="media_url" validate={required()} fullWidth />
    </Grid>
    <Grid item xs={12} md={6}>
      <MyDateTimeInput label="Start Time" source="start_time" validate={required()} fullWidth />
    </Grid>
    <Grid item xs={12} md={6}>
      <MyDateTimeInput label="End Time" source="end_time" validate={required()} fullWidth />
    </Grid>
    <Grid item xs={12}>
      <PlatformCheckboxGroupInput />
    </Grid>
    <Grid item xs={12}>
      <AdsVideoRegionInput />
    </Grid>
    <FormDataConsumer>
      {({ formData }) => (
        <Grid item xs={12} md={6}>
          <BooleanInput source="is_homepage" disabled={isStartupAd(formData?.type)} helperText="To make a transparent ad appear on homepage, enable Is homepage." />
        </Grid>
      )}
    </FormDataConsumer>
    <Grid item xs={12} md={6}>
      <NumberInput source="timeout" validate={required()} min={0} fullWidth />
    </Grid>
    <Grid item xs={12}>
      <ArrayInput source="frequency_cap" label="Frequency Cap">
        <SimpleFormIterator disableReordering>
          <NumberInput label="Impression Value" source="imp_value" validate={required()} min={1} fullWidth />
          <NumberInput label="Period Value" source="period_value" validate={required()} min={1} fullWidth />
          <SelectInput label="Period Unit" source="period_unit" validate={required()} fullWidth choices={[{ id: "day", name: "Day" }, { id: "hour", name: "Hour" }, { id: "minutes", name: "Minutes" }]} />
        </SimpleFormIterator>
      </ArrayInput>
    </Grid>
    <FormDataConsumer>
      {({ formData }) => <AdsVideoRelations disabled={isRelationLocked(formData)} />}
    </FormDataConsumer>
  </Grid>
);

const AdsVideoPermissionDenied = () => <div>Permission denied.</div>;
const defaultValues = { is_homepage: false, timeout: 6000, category_profile_class: defaultCategoryProfileClass, region: ["HK", "MO"], frequency_cap: [{ imp_value: 5, period_value: 1, period_unit: "day" }], locations: { allowlist: { countries: ["HK", "MO"] } } };

export const AdsVideoCreate = (props) => {
  const { permissions } = usePermissions();
  if (permissions && !canEditAdsVideo(permissions)) return <AdsVideoPermissionDenied />;
  return <Create {...props} transform={transformAdsVideo}><SimpleForm validate={validateAdsVideo} initialValues={defaultValues}><AdsVideoForm /></SimpleForm></Create>;
};

export const AdsVideoEdit = (props) => {
  const { permissions } = usePermissions();
  if (permissions && !canEditAdsVideo(permissions)) return <AdsVideoPermissionDenied />;
  return <Edit {...props} transform={transformAdsVideo} mutationMode="pessimistic"><SimpleForm validate={validateAdsVideo}><AdsVideoForm /></SimpleForm></Edit>;
};

export const AdsVideoShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" label="ID" />
      <TextField source="type" />
      <TextField source="media_url" label="Media URL" />
      <BooleanField source="is_homepage" />
      <FunctionField label="Programme IDs" render={(record) => idList(record.programme_id)} />
      <FunctionField label="Page IDs" render={(record) => idList(record.page_id)} />
      <FunctionField label="Category IDs" render={(record) => idList(record.category_id)} />
      <PlatformChipField />
      <TextField source="timeout" />
      <MyDateField source="start_time" showTime />
      <MyDateField source="end_time" showTime />
      <MyDateField source="created_at" showTime />
      <MyDateField source="modified_at" showTime />
    </SimpleShowLayout>
  </Show>
);